import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import StatusBadge, { UrgencyFlag } from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { CardSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtTime } from "@/lib/format";
import { Plus, Wrench, Calendar, Clock } from "lucide-react";

const TABS = [
  { key: "all", label: "All" },
  { key: "unscheduled", label: "Unscheduled" },
  { key: "scheduled", label: "Scheduled" },
  { key: "in_progress", label: "In Progress" },
  { key: "done", label: "Complete" },
];

export default function Jobs() {
  const [tab, setTab] = useState("all");
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    setJobs(null);
    (async () => {
      const params = tab === "all" ? {} : { status: tab };
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    })();
  }, [tab]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">Jobs</h1>
        <Link to="/jobs/new" data-testid="new-job-btn">
          <Button className="h-12 px-4 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl">
            <Plus className="w-5 h-5 mr-1.5" />
            <span className="hidden sm:inline">New Job</span>
            <span className="sm:hidden">New</span>
          </Button>
        </Link>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`h-10 px-4 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              tab === t.key ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {jobs === null ? (
        <CardSkeleton count={4} />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title="No jobs here"
          description="When you create jobs they'll show up here."
          action={
            <Link to="/jobs/new">
              <Button className="h-12 px-5 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl">
                <Plus className="w-5 h-5 mr-1.5" /> Create job
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-2">
          {jobs.map((j) => (
            <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`job-row-${j.id}`}>
              <div className={`bg-white rounded-2xl border p-4 hover:shadow-sm transition-all active:scale-[0.99] mb-2 ${j.urgency === "emergency" ? "border-red-300 ring-2 ring-red-100" : "border-slate-200 hover:border-blue-300"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <UrgencyFlag urgency={j.urgency} />
                      <div className="font-semibold text-slate-900 truncate">{j.title}</div>
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      {j.customer_name} • {j.tech_name} • {j.job_type || "Other"}
                    </div>
                    <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                      {j.scheduled_date && (
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmtDate(j.scheduled_date)}</span>
                      )}
                      {j.scheduled_time && (
                        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{fmtTime(j.scheduled_time)}</span>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={j.status} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
